"use client";

import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import {
  HiAcademicCap,
  HiArrowLeft,
  HiArrowRight,
  HiBriefcase,
} from "react-icons/hi";
import {
  GiFire,
  GiGlobe,
  GiNewspaper,
  GiPolarStar,
  GiScrollUnfurled,
} from "react-icons/gi";

function SectionCard({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
      rounded="2xl"
      p={{ base: 5, md: 7 }}
    >
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="0.08em"
        textTransform="uppercase"
        color="primary.300"
        mb={2}
      >
        {eyebrow}
      </Text>
      <Text
        fontSize={{ base: "xl", md: "2xl" }}
        fontWeight="bold"
        color="white"
        mb={4}
        lineHeight="short"
      >
        {title}
      </Text>
      {children}
    </Box>
  );
}

export function ResumeBenchmarkGuideClient() {
  const t = useTranslations("resume_benchmark");
  const locale = useLocale();
  const router = useRouter();

  const scoreBands = [
    {
      label: t("guide.bands.excellent"),
      range: t("guide.bands.excellentRange"),
      color: "green.400",
    },
    {
      label: t("guide.bands.good"),
      range: t("guide.bands.goodRange"),
      color: "yellow.400",
    },
    {
      label: t("guide.bands.fair"),
      range: t("guide.bands.fairRange"),
      color: "orange.400",
    },
    {
      label: t("guide.bands.needsWork"),
      range: t("guide.bands.needsWorkRange"),
      color: "red.400",
    },
  ] as const;

  const metrics: Array<{
    icon: React.ElementType;
    iconColor: string;
    title: string;
    description: string;
  }> = [
    {
      icon: GiPolarStar,
      iconColor: "yellow.400",
      title: t("guide.metrics.overall.title"),
      description: t("guide.metrics.overall.desc"),
    },
    {
      icon: GiFire,
      iconColor: "orange.500",
      title: t("guide.metrics.experience.title"),
      description: t("guide.metrics.experience.desc"),
    },
    {
      icon: HiBriefcase,
      iconColor: "yellow.700",
      title: t("guide.metrics.skills.title"),
      description: t("guide.metrics.skills.desc"),
    },
    {
      icon: GiGlobe,
      iconColor: "blue.400",
      title: t("guide.metrics.language.title"),
      description: t("guide.metrics.language.desc"),
    },
    {
      icon: HiAcademicCap,
      iconColor: "teal.400",
      title: t("guide.metrics.education.title"),
      description: t("guide.metrics.education.desc"),
    },
    {
      icon: GiScrollUnfurled,
      iconColor: "green.400",
      title: t("guide.metrics.certificates.title"),
      description: t("guide.metrics.certificates.desc"),
    },
    {
      icon: GiNewspaper,
      iconColor: "purple.400",
      title: t("guide.metrics.ats.title"),
      description: t("guide.metrics.ats.desc"),
    },
  ];

  return (
    <Container maxW="900px" px={{ base: 4, md: 8 }} py={{ base: 10, md: 14 }}>
      <VStack align="stretch" gap={{ base: 8, md: 10 }}>
        <Box>
          <Button
            variant="ghost"
            size="sm"
            color="whiteAlpha.700"
            mb={5}
            bg="transparent"
            px={0}
            _hover={{
              color: "white",
              bg: "primary.500",
              px: 3,
              shadow: "shadow-primary-xl",
            }}
            onClick={() => router.push(`/${locale}/resume-benchmark`)}
            transition="all 0.2s ease-in-out"
          >
            <Icon as={HiArrowLeft} />
            {t("guide.back")}
          </Button>

          <VStack align="flex-start" gap={3}>
            <Text
              fontSize="xs"
              fontWeight="bold"
              letterSpacing="widest"
              textTransform="uppercase"
              color="primary.300"
            >
              {t("guide.eyebrow")}
            </Text>
            <Text
              as="h1"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="black"
              color="white"
              lineHeight="1.15"
            >
              {t("guide.title")}
            </Text>
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color="whiteAlpha.600"
              maxW="640px"
              lineHeight="tall"
            >
              {t("guide.intro")}
            </Text>
          </VStack>
        </Box>

        <SectionCard
          eyebrow={t("guide.s1.eyebrow")}
          title={t("guide.s1.title")}
        >
          <VStack align="stretch" gap={3}>
            <Text color="whiteAlpha.700" fontSize="sm" lineHeight="tall">
              {t("guide.s1.p1")}
            </Text>
            <Text color="whiteAlpha.700" fontSize="sm" lineHeight="tall">
              {t("guide.s1.p2")}
            </Text>
          </VStack>
        </SectionCard>

        <SectionCard
          eyebrow={t("guide.s2.eyebrow")}
          title={t("guide.s2.title")}
        >
          <VStack align="stretch" gap={4}>
            <Text color="whiteAlpha.700" fontSize="sm" lineHeight="tall">
              {t("guide.s2.intro")}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
              <Box
                p={4}
                rounded="xl"
                bg="blackAlpha.400"
                border="1px solid"
                borderColor="whiteAlpha.100"
              >
                <Text fontWeight="bold" color="green.300" fontSize="sm" mb={1}>
                  {t("guide.s2.freeNoAccount.title")}
                </Text>
                <Text fontSize="sm" color="whiteAlpha.600" lineHeight="tall">
                  {t("guide.s2.freeNoAccount.body")}
                </Text>
              </Box>
              <Box
                p={4}
                rounded="xl"
                bg="blackAlpha.400"
                border="1px solid"
                borderColor="whiteAlpha.100"
              >
                <Text
                  fontWeight="bold"
                  color="primary.300"
                  fontSize="sm"
                  mb={1}
                >
                  {t("guide.s2.freeWithAccount.title")}
                </Text>
                <Text fontSize="sm" color="whiteAlpha.600" lineHeight="tall">
                  {t("guide.s2.freeWithAccount.body")}
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </SectionCard>

        <SectionCard
          eyebrow={t("guide.s3.eyebrow")}
          title={t("guide.s3.title")}
        >
          <VStack align="stretch" gap={6}>
            <Text color="whiteAlpha.700" fontSize="sm" lineHeight="tall">
              {t("guide.s3.intro")}
            </Text>

            <Grid
              templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
              gap={3}
            >
              {scoreBands.map((band) => (
                <Box
                  key={band.label}
                  p={3}
                  rounded="xl"
                  bg="blackAlpha.400"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  textAlign="center"
                >
                  <Text fontSize="sm" fontWeight="bold" color={band.color}>
                    {band.label}
                  </Text>
                  <Text fontSize="xs" color="whiteAlpha.500" mt={1}>
                    {band.range}
                  </Text>
                </Box>
              ))}
            </Grid>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
              {metrics.map((metric) => (
                <Box
                  key={metric.title}
                  p={4}
                  rounded="xl"
                  bg="blackAlpha.400"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                >
                  <HStack gap={2} mb={2}>
                    <Icon
                      as={metric.icon}
                      color={metric.iconColor}
                      boxSize={5}
                    />
                    <Text fontSize="sm" fontWeight="bold" color="white">
                      {metric.title}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="whiteAlpha.600" lineHeight="tall">
                    {metric.description}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </SectionCard>

        <Flex
          direction={{ base: "column", sm: "row" }}
          gap={3}
          justify="flex-start"
          pt={2}
        >
          <Button
            size="md"
            rounded="xl"
            bg="primary.500"
            color="white"
            fontWeight="bold"
            _hover={{ bg: "primary.600" }}
            onClick={() => router.push(`/${locale}/resume-benchmark`)}
          >
            {t("guide.cta.try")}
            <Icon as={HiArrowRight} />
          </Button>
          <Button
            size="md"
            rounded="xl"
            visual="outline"
            borderColor="whiteAlpha.300"
            color="white"
            fontWeight="bold"
            _hover={{ bg: "whiteAlpha.100", borderColor: "whiteAlpha.500" }}
            onClick={() => router.push(`/${locale}/auth/login`)}
          >
            {t("guide.cta.signIn")}
          </Button>
        </Flex>
      </VStack>
    </Container>
  );
}
